import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { collection, getDocs, onSnapshot } from 'firebase/firestore';
import Constants from 'expo-constants';
import { firestore } from '../services/firebase';
import { PRODUCTS, PLANS, COURSES } from '../constants/entities';
import { attachCatalogAccessIds } from '../utils/catalogAccessIds';
import {
  filterPlanDocsForCatalog,
  filterProductDocsForCatalog,
  getCatalogTenantKey,
  isGuruOrPagarmeTenant,
} from '../utils/catalogTenantScope';
import { useRemoteConfigContext } from '../contexts/useRemoteConfigContext';
import { queryClient } from './queryClient';

const CATALOG_PRODUCTS_QUERY_KEY = ['catalogProducts'];

export { CATALOG_PRODUCTS_QUERY_KEY };

const getPlanFrequencyIds = (data) =>
  Object.values(data?.productIds ?? {}).filter((id) => !!id);

const docToCatalogProduct = (docSnap, type) => {
  const data = docSnap.data();
  const equivalentIds = Array.isArray(data.equivalentIds) ? data.equivalentIds : [];

  let productId = data.productId ?? docSnap.id;
  let ownIds;

  if (type === 'plan') {
    const frequencyIds = getPlanFrequencyIds(data);
    productId = frequencyIds[0] ?? docSnap.id;
    ownIds = frequencyIds.map((id) => ({ id, type: 'plan' }));
  } else {
    ownIds = [{ id: productId, type }];
  }

  const catalogImage = data.catalogImage ?? (data.image ? { url: data.image } : undefined);

  return {
    id: docSnap.id,
    productId,
    type,
    name: data.name ?? data.title ?? data.titleCourse ?? '',
    archived: !!data.archived,
    order: data.order ?? 0,
    catalogImage,
    showInCatalog: data.showInCatalog,
    productCategoryId: data.productCategoryId ?? null,
    ownIds,
    equivalentIds,
  };
};

function sortCatalogProducts(all) {
  return attachCatalogAccessIds(all.sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));
}

function buildCatalogProductsFromProductDocs(docs = []) {
  return sortCatalogProducts(
    filterProductDocsForCatalog(docs).map((docSnap) =>
      docToCatalogProduct(docSnap, 'product')
    )
  );
}

function buildCatalogProductsFromStripeDocs(planDocs = [], courseDocs = []) {
  return sortCatalogProducts([
    ...filterPlanDocsForCatalog(planDocs).map((docSnap) =>
      docToCatalogProduct(docSnap, 'plan')
    ),
    ...courseDocs.map((docSnap) => docToCatalogProduct(docSnap, 'course')),
  ]);
}

/**
 * Carrega só o que a aba Produtos do admin gerencia para o tenant atual:
 * Guru/Pagarme → collection `products` (Guru, Pagarme, IAP);
 * Stripe → `plans` (pais) + `courses`.
 */
export async function fetchCatalogProducts(tenantOptions = {}) {
  const guruUserToken =
    tenantOptions.guruUserToken ?? Constants.manifest?.extra?.guruUserToken;
  const paymentGateway = tenantOptions.paymentGateway ?? '';
  const isGuru = isGuruOrPagarmeTenant({ guruUserToken, paymentGateway });

  if (isGuru) {
    const productsSnap = await getDocs(collection(firestore, PRODUCTS));
    return buildCatalogProductsFromProductDocs(productsSnap.docs);
  }

  const [plansSnap, coursesSnap] = await Promise.all([
    getDocs(collection(firestore, PLANS)),
    getDocs(collection(firestore, COURSES)),
  ]);

  return buildCatalogProductsFromStripeDocs(plansSnap.docs, coursesSnap.docs);
}

export function getCatalogProductsQueryKey(tenantOptions = {}) {
  const guruUserToken =
    tenantOptions.guruUserToken ?? Constants.manifest?.extra?.guruUserToken;
  const paymentGateway = tenantOptions.paymentGateway ?? '';
  const tenantKey = getCatalogTenantKey({ guruUserToken, paymentGateway });
  return [...CATALOG_PRODUCTS_QUERY_KEY, tenantKey];
}

/** Mantém o cache do react-query sincronizado com Firestore (onSnapshot). */
export function subscribeCatalogProducts(tenantOptions = {}) {
  const queryKey = getCatalogProductsQueryKey(tenantOptions);
  const guruUserToken =
    tenantOptions.guruUserToken ?? Constants.manifest?.extra?.guruUserToken;
  const paymentGateway = tenantOptions.paymentGateway ?? '';
  const isGuru = isGuruOrPagarmeTenant({ guruUserToken, paymentGateway });

  const publish = (all) => {
    queryClient.setQueryData(queryKey, all);
  };

  if (isGuru) {
    return onSnapshot(
      collection(firestore, PRODUCTS),
      (snapshot) => publish(buildCatalogProductsFromProductDocs(snapshot.docs)),
      (error) => {
        console.error('[subscribeCatalogProducts] erro no snapshot de products:', error);
      }
    );
  }

  let planDocs = [];
  let courseDocs = [];

  const publishStripe = () => {
    publish(buildCatalogProductsFromStripeDocs(planDocs, courseDocs));
  };

  const unsubscribePlans = onSnapshot(
    collection(firestore, PLANS),
    (snapshot) => {
      planDocs = snapshot.docs;
      publishStripe();
    },
    (error) => {
      console.error('[subscribeCatalogProducts] erro no snapshot de plans:', error);
    }
  );

  const unsubscribeCourses = onSnapshot(
    collection(firestore, COURSES),
    (snapshot) => {
      courseDocs = snapshot.docs;
      publishStripe();
    },
    (error) => {
      console.error('[subscribeCatalogProducts] erro no snapshot de courses:', error);
    }
  );

  return () => {
    unsubscribePlans();
    unsubscribeCourses();
  };
}

export function useCatalogProductsSnapshot(enabled = true) {
  const config = useRemoteConfigContext();
  const guruUserToken = Constants.manifest?.extra?.guruUserToken;
  const paymentGateway = config.paymentGateway;

  useEffect(() => {
    if (!enabled) return undefined;

    return subscribeCatalogProducts({ guruUserToken, paymentGateway });
  }, [enabled, guruUserToken, paymentGateway]);
}

export const useCatalogProducts = (enabled = true) => {
  const config = useRemoteConfigContext();
  const guruUserToken = Constants.manifest?.extra?.guruUserToken;
  const tenantKey = getCatalogTenantKey({
    guruUserToken,
    paymentGateway: config.paymentGateway,
  });

  return useQuery({
    queryKey: [...CATALOG_PRODUCTS_QUERY_KEY, tenantKey],
    enabled,
    queryFn: () =>
      fetchCatalogProducts({
        guruUserToken,
        paymentGateway: config.paymentGateway,
      }),
  });
};
